import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import emailjs from '@emailjs/browser';
import { Routes, Route } from 'react-router-dom';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import { Link } from 'react-router-dom';
import Technology from './Technology';
import FAQ from './FAQ';
import HeroShowcase from './HeroShowcase';
import AboutUs from './AboutUs';
import Solutions from './Solutions';
import Glossary from './Glossary';
import WhyNormalQRFails from './WhyNormalQRFails';


export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/technology" element={<Technology />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/glossary" element={<Glossary />} />
      <Route path="/why-normal-qr-fails" element={<WhyNormalQRFails />} />
    </Routes>
  );
}
/*
  This component is a faithful React conversion of the original QRmor
  index.html. Markup, classes, ids and inline styles are preserved as-is.
  The vanilla-JS behaviour from the original <script> block (Leaflet map,
  scroll-reveal animations, count-up stats, and the demo request form) is
  reproduced inside useEffect hooks, using the same DOM APIs / ids the
  original script used (getElementById, querySelectorAll, etc).
*/

function Home() {
  const mapInitedRef = useRef(false);
  const [ccOpen, setCcOpen] = useState(false);
  const [ccSearch, setCcSearch] = useState('');
  const [ccSelected, setCcSelected] = useState({ flag: '🇮🇳', code: '+91' });
  const ccWrapRef = useRef(null);

  useEffect(() => {
    document.title = 'QRmor | Non-Clonable Anti-Counterfeit QR Codes & Authentication';
    const metaDesc = document.querySelector('meta[name="description"]');
    const content = 'Stop fakes in their tracks with QRmor\'s Anti-Counterfeit, Non-Clonable, Dynamic QR codes — zero hardware, zero software, zero apps, just scan for instant verification, clone detection, and location tracing.';
    if (metaDesc) {
      metaDesc.setAttribute('content', content);
    } else {
      const tag = document.createElement('meta');
      tag.name = 'description';
      tag.content = content;
      document.head.appendChild(tag);
    }
  }, []);

  function handleNavClick(id) {
    return function (e) {
      e.preventDefault();
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    };
  }

  const COUNTRY_CODES = [
    { flag: '🇦🇫', code: '+93', name: 'Afghanistan' },
    { flag: '🇦🇱', code: '+355', name: 'Albania' },
    { flag: '🇩🇿', code: '+213', name: 'Algeria' },
    { flag: '🇦🇷', code: '+54', name: 'Argentina' },
    { flag: '🇦🇺', code: '+61', name: 'Australia' },
    { flag: '🇦🇹', code: '+43', name: 'Austria' },
    { flag: '🇧🇭', code: '+973', name: 'Bahrain' },
    { flag: '🇧🇩', code: '+880', name: 'Bangladesh' },
    { flag: '🇧🇪', code: '+32', name: 'Belgium' },
    { flag: '🇧🇷', code: '+55', name: 'Brazil' },
    { flag: '🇨🇦', code: '+1', name: 'Canada' },
    { flag: '🇨🇳', code: '+86', name: 'China' },
    { flag: '🇩🇰', code: '+45', name: 'Denmark' },
    { flag: '🇪🇬', code: '+20', name: 'Egypt' },
    { flag: '🇫🇷', code: '+33', name: 'France' },
    { flag: '🇩🇪', code: '+49', name: 'Germany' },
    { flag: '🇬🇷', code: '+30', name: 'Greece' },
    { flag: '🇭🇰', code: '+852', name: 'Hong Kong' },
    { flag: '🇮🇳', code: '+91', name: 'India' },
    { flag: '🇮🇩', code: '+62', name: 'Indonesia' },
    { flag: '🇮🇪', code: '+353', name: 'Ireland' },
    { flag: '🇮🇱', code: '+972', name: 'Israel' },
    { flag: '🇮🇹', code: '+39', name: 'Italy' },
    { flag: '🇯🇵', code: '+81', name: 'Japan' },
    { flag: '🇰🇪', code: '+254', name: 'Kenya' },
    { flag: '🇰🇼', code: '+965', name: 'Kuwait' },
    { flag: '🇲🇾', code: '+60', name: 'Malaysia' },
    { flag: '🇲🇽', code: '+52', name: 'Mexico' },
    { flag: '🇳🇱', code: '+31', name: 'Netherlands' },
    { flag: '🇳🇿', code: '+64', name: 'New Zealand' },
    { flag: '🇳🇬', code: '+234', name: 'Nigeria' },
    { flag: '🇵🇰', code: '+92', name: 'Pakistan' },
    { flag: '🇵🇭', code: '+63', name: 'Philippines' },
    { flag: '🇶🇦', code: '+974', name: 'Qatar' },
    { flag: '🇷🇺', code: '+7', name: 'Russia' },
    { flag: '🇸🇦', code: '+966', name: 'Saudi Arabia' },
    { flag: '🇸🇬', code: '+65', name: 'Singapore' },
    { flag: '🇿🇦', code: '+27', name: 'South Africa' },
    { flag: '🇰🇷', code: '+82', name: 'South Korea' },
    { flag: '🇪🇸', code: '+34', name: 'Spain' },
    { flag: '🇱🇰', code: '+94', name: 'Sri Lanka' },
    { flag: '🇸🇪', code: '+46', name: 'Sweden' },
    { flag: '🇨🇭', code: '+41', name: 'Switzerland' },
    { flag: '🇹🇼', code: '+886', name: 'Taiwan' },
    { flag: '🇹🇭', code: '+66', name: 'Thailand' },
    { flag: '🇦🇪', code: '+971', name: 'UAE' },
    { flag: '🇬🇧', code: '+44', name: 'United Kingdom' },
    { flag: '🇺🇸', code: '+1', name: 'United States' },
    { flag: '🇻🇳', code: '+84', name: 'Vietnam' }
  ];

  const filteredCodes = ccSearch
    ? COUNTRY_CODES.filter(c =>
      c.code.includes(ccSearch) ||
      c.name.toLowerCase().includes(ccSearch.toLowerCase())
    )
    : COUNTRY_CODES;

  /* ---------- VERIFICATION MAP ---------- */
  useEffect(() => {
    if (mapInitedRef.current) return; // guard against React StrictMode double-invoke
    mapInitedRef.current = true;

    function initMap() {
      if (typeof window.L === 'undefined') return;
      const L = window.L;
      const mapEl = document.getElementById('verifyMap');
      if (!mapEl || mapEl._leaflet_id) return; // already initialised

      const map = L.map('verifyMap', {
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true
      }).setView([19.5, 74], 4.4);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(map);

      function pinIcon(isGenuine) {
        return L.divIcon({
          className: '',
          html: '<div style="width:16px;height:16px;background:' + (isGenuine ? '#3FA79A' : '#E4572E') +
            ';border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
          iconSize: [16, 16],
          iconAnchor: [8, 16]
        });
      }

      // Replace with real data, e.g. fetched via
      // axios.get(baseURL + 'verification/fetchAllLocationDetails/all')
      const verifications = [
        { lat: 23.03, lng: 72.58, product: 'Sample A', batch: 'A101', result: '1', state: 'Gujarat', district: 'Ahmedabad', time: '20 Jul 2026 10:15 am' },
        { lat: 23.05, lng: 72.60, product: 'Sample A', batch: 'A102', result: '1', state: 'Gujarat', district: 'Ahmedabad', time: '20 Jul 2026 11:02 am' },
        { lat: 12.97, lng: 77.59, product: 'Sample B', batch: 'B221', result: '1', state: 'Karnataka', district: 'Bengaluru Urban', time: '20 Jul 2026 09:40 am' },
        { lat: 12.99, lng: 77.61, product: 'Sample B', batch: 'B222', result: '1', state: 'Karnataka', district: 'Bengaluru Urban', time: '20 Jul 2026 12:20 pm' },
        { lat: 28.60, lng: 77.20, product: 'Sample C', batch: 'CC', result: '0', state: 'Delhi', district: 'Central Delhi', time: '20 Jul 2026 08:05 am' },
        { lat: 28.62, lng: 77.22, product: 'Sample C', batch: 'CC', result: '0', state: 'Delhi', district: 'North Delhi', time: '20 Jul 2026 08:47 am' },
        { lat: 28.58, lng: 77.24, product: 'Sample C', batch: 'CC', result: '0', state: 'Delhi', district: 'South Delhi', time: '20 Jul 2026 09:15 am' }
      ];

      const clusterGroup = L.markerClusterGroup({
        maxClusterRadius: 60,
        spiderfyOnMaxZoom: true,   // reveals individual pins once fully zoomed in
        showCoverageOnHover: false,
        iconCreateFunction: function (cluster) {
          const children = cluster.getAllChildMarkers();
          const hasFlagged = children.some(m => m.options.verificationResult === '0');
          return L.divIcon({
            className: '',
            html: '<div class="' + (hasFlagged ? 'cluster-flag' : 'cluster-genuine') + '">' + cluster.getChildCount() + '</div>',
            iconSize: hasFlagged ? [30, 30] : [34, 34]
          });
        }
      });

      verifications.forEach(v => {
        const isGenuine = v.result === '1';
        const marker = L.marker([v.lat, v.lng], {
          icon: pinIcon(isGenuine),
          verificationResult: v.result
        }).bindPopup(
          '<div class="qrmor-popup">' +
          '<div class="row"><span class="k">Product</span><span class="v">' + v.product + '</span></div>' +
          (isGenuine ? '<div class="row"><span class="k">Batch</span><span class="v">' + v.batch + '</span></div>' : '') +
          '<div class="row"><span class="k">Result</span><span class="v ' + (isGenuine ? 'ok' : 'bad') + '">' + (isGenuine ? 'Verified' : 'Not-Verified') + '</span></div>' +
          '<div class="row"><span class="k">State</span><span class="v">' + v.state + '</span></div>' +
          '<div class="row"><span class="k">District</span><span class="v">' + v.district + '</span></div>' +
          '<div class="row"><span class="k">Time</span><span class="v">' + v.time + '</span></div>' +
          '</div>'
        );
        clusterGroup.addLayer(marker);
      });

      map.addLayer(clusterGroup);
    }

    // Leaflet + markercluster are loaded via <script> tags in public/index.html.
    // If they're already on window, init immediately; otherwise wait for them.
    if (window.L && window.L.markerClusterGroup) {
      initMap();
    } else {
      const check = setInterval(() => {
        if (window.L && window.L.markerClusterGroup) {
          clearInterval(check);
          initMap();
        }
      }, 50);
      return () => clearInterval(check);
    }
  }, []);

  /* ---------- SCROLL REVEAL ---------- */
  useEffect(() => {
    const revealEls = document.querySelectorAll('.reveal');
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    revealEls.forEach(el => revealObserver.observe(el));
    return () => revealObserver.disconnect();
  }, []);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (ccWrapRef.current && !ccWrapRef.current.contains(e.target)) {
        setCcOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  /* ---------- COUNT-UP STATS ---------- */
  /* ---------- COUNT-UP STATS ---------- */
  useEffect(() => {
    const counters = document.querySelectorAll('.stat-num');
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (!el.hasAttribute('data-count')) {
          counterObserver.unobserve(el);
          return;
        }
        const target = parseInt(el.getAttribute('data-count'), 10) || 0;
        const prefixEl = el.querySelector('.stat-prefix');
        const prefix = prefixEl ? prefixEl.outerHTML : '';
        const suffix = el.querySelector('span:not(.stat-prefix)') ? el.querySelector('span:not(.stat-prefix)').outerHTML : '';
        const duration = 1100;
        const start = performance.now();
        function tick(now) {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const val = Math.round(eased * target);
          el.innerHTML = prefix + val + suffix;
          if (progress < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
        counterObserver.unobserve(el);
      });
    }, { threshold: 0.4 });
    counters.forEach(el => counterObserver.observe(el));
    return () => counterObserver.disconnect();
  }, []);

  /* ---------- DEMO REQUEST FORM ----------
  NOTE for integration: this front-end cannot safely call the Zammad API
  directly (it would expose credentials in the browser). Point this at a
  small backend endpoint you control — e.g. POST /api/demo-request — which
  then creates a ticket in Zammad using a server-side API token.
  */
  useEffect(() => {
    const demoSubmit = document.getElementById('demoSubmit');
    const formStatus = document.getElementById('formStatus');
    if (!demoSubmit || !formStatus) return;

    const phoneInputEl = document.getElementById('fPhone');
    function restrictPhoneInput() {
      phoneInputEl.value = phoneInputEl.value.replace(/\D/g, '').slice(0, 10);
    }
    phoneInputEl?.addEventListener('input', restrictPhoneInput);

    async function handleSubmit() {
      const nameEl = document.getElementById('fName');
      const emailEl = document.getElementById('fEmail');
      const phoneEl = document.getElementById('fPhone');



      const companyEl = document.getElementById('fCompany');
      const messageEl = document.getElementById('fMessage');

      const name = nameEl.value.trim();
      const email = emailEl.value.trim();
      const countryCode = ccSelected.code;
      const phoneDigitsOnly = phoneEl.value.trim().replace(/\D/g, '');
      const phone = countryCode + ' ' + phoneDigitsOnly;
      const company = companyEl.value.trim();
      const message = messageEl.value.trim();

      const nameErrorEl = document.getElementById('fNameError');
      const companyErrorEl = document.getElementById('fCompanyError');
      const emailErrorEl = document.getElementById('fEmailError');
      const phoneErrorEl = document.getElementById('fPhoneError');

      const messageErrorEl = document.getElementById('fMessageError');

      [nameErrorEl, companyErrorEl, emailErrorEl, phoneErrorEl, messageErrorEl].forEach(el => { el.textContent = ''; });

      let hasError = false;

      if (!name) {
        nameErrorEl.textContent = 'Name is required.';
        hasError = true;
      } else if (!/^[A-Za-z][A-Za-z\s.'-]*$/.test(name)) {
        nameErrorEl.textContent = 'Name should only contain letters.';
        hasError = true;
      }

      if (!company) {
        companyErrorEl.textContent = 'Company name is required.';
        hasError = true;
      }

      if (!email) {
        emailErrorEl.textContent = 'Email is required.';
        hasError = true;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        emailErrorEl.textContent = 'Please enter a valid email address.';
        hasError = true;
      }

      if (!phoneDigitsOnly) {
        phoneErrorEl.textContent = 'Phone number is required.';
        hasError = true;
      } else if (phoneDigitsOnly.length !== 10) {
        phoneErrorEl.textContent = 'Phone number must be exactly 10 digits.';
        hasError = true;
      }

      const wordCount = message ? message.split(/\s+/).length : 0;
      if (wordCount > 150) {
        messageErrorEl.textContent = `Message is ${wordCount} words — please keep it under 150.`;
        hasError = true;
      }

      if (hasError) {
        formStatus.textContent = '';
        formStatus.className = 'form-status';
        return;
      }

      demoSubmit.disabled = true;
      demoSubmit.textContent = 'Sending...';
      formStatus.textContent = '';
      formStatus.className = 'form-status';

      try {
        await emailjs.send(
          'service_oeaqxag',
          'template_mlhs6ha',
          { name, email, phone, company, message },
          'u8rOqEr5nj7aqo0jq'
        );

        formStatus.textContent = 'Thanks, ' + name.split(' ')[0] + " — we'll be in touch shortly.";
        formStatus.className = 'form-status ok';
        demoSubmit.textContent = 'Request sent';
        demoSubmit.style.opacity = '0.75';
        // clear the form
        nameEl.value = '';
        emailEl.value = '';
        phoneEl.value = '';
        setCcSelected({ flag: '🇮🇳', code: '+91' });
        setCcSearch('');
        companyEl.value = '';
        messageEl.value = '';

        // re-enable the button after a short delay
        setTimeout(() => {
          demoSubmit.disabled = false;
          demoSubmit.textContent = 'Request a demo';
          demoSubmit.style.opacity = '1';
        }, 3000);
      } catch (err) {
        formStatus.textContent = 'Something went wrong sending your request. Please try again or email us directly.';
        formStatus.className = 'form-status err';
        demoSubmit.disabled = false;
        demoSubmit.textContent = 'Request a demo';
      }
    }

    demoSubmit.addEventListener('click', handleSubmit);
    return () => {
      demoSubmit.removeEventListener('click', handleSubmit);
      phoneInputEl?.removeEventListener('input', restrictPhoneInput);
    };
  }, [ccSelected]);

  return (
    <>
      <header>
        <nav className="wrap">
          <div className="brand">
            <img src="/assets/QRmor.png" alt="QRmor logo" />
            <div className="divider" />
            <img src="/assets/Scinova.png" alt="Scinova Scientifics logo" className="brand-scinova-logo" />
            <div className="brand-scinova-text">
              <span className="parent">Scinova Scientifics</span>
              <span className="brand-tagline"> -Science as a Service</span>
            </div>
          </div>

          <div className="navlinks">
            <a href="#how" onClick={handleNavClick('how')}>How it works</a>
            <a href="#why" onClick={handleNavClick('why')}>Why QRmor</a>
            <a href="#features" onClick={handleNavClick('features')}>Features</a>
            <a href="#usecases" onClick={handleNavClick('usecases')}>Who it's for</a>
            <Link to="/why-normal-qr-fails">Why normal QR fails</Link>
          </div>
          <a href="#contact" onClick={handleNavClick('contact')} className="btn btn-primary btn-sm">Request a demo</a>
        </nav>
      </header>
      <section className="hero">
        <div className="wrap">
          <div>
            <span className="eyebrow">Anti-counterfeit QR Technology</span>
            <p className="hero-tagline"><b>QRmor</b> is a first-of-its-kind, anti-counterfeit <b>QR code</b>, developed with an aim to protect brand assets.</p>

            <h1>One QRmor code.<br />No copy ever <em>passes</em>.</h1>

            <ul className="lead-list">
              <li>
                QRmor codes are <b>first-of-its-kind, anti-counterfeit, non-clonable.</b></li>
              <li>When a QRmor's cloned or duplicated QR code is scanned, the <b>QRmor Authenticator flags</b> it instantly, with the location logged for you.
              </li>
              <li>QRmor codes are built <b>to secure brands and assets</b> — engineered with deep tech to make duplication extremely difficult.</li>
              <li>QRmor is also aligned with <b>GAMP and 21 CFR Part 11 guideline</b> requirements for pharma, apart from other industry verticals.</li>
              <li><b>Pre-printed convenience</b>- one-time artwork integration of QRmor codes, skipping the hassle of separate online printing.</li>
            </ul>

            <div className="trust-line"><span className="dot" /> Powered By <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC</a> </div>
          </div>

          <div className="hero-image">
            <div className="section-head section-head-wide">
              <span className="eyebrow">Live activity</span>
              <h2>See verification activity as it happens.</h2>
              <p>Every scan — genuine or flagged — is logged with its location.</p>
            </div>

            <div className="map-card">
              <div className="map-top">
                <span className="label">REPRESENTATIVE VERIFICATION ACTIVITY</span>
                <span className="label">map</span>
              </div>
              <div className="map-stage">
                <div id="verifyMap" />
              </div>
              <div className="map-legend">
                <span><i className="legend-dot genuine" />Verified Cluster</span>
                <span><i className="legend-dot flag" />Not-Verified flagged</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="stats" className="reveal">
        <div className="wrap">
          <div className="stats-strip">

            {/* 1st Card */}
            <div className="stat-card">
              <span className="stat-num stat-heading">QRmor — The Code that can't be Copied</span>
              <div className="stat-label">
                State-of-the-art anti-counterfeit technology.
              </div>
            </div>

            {/* 2nd Card - moved from last position */}
            {/* <div className="stat-card">
              <span className="stat-num infinity-symbol">∞</span>
              <div className="stat-label">No limit on how many QR codes you can generate.*under an annual plan</div>
            </div> */}
            <div className="stat-card">
              <span className="stat-num infinity-symbol">∞</span>
              <div className="stat-label">
                No limit on how many QR codes you can generate.
                <br />
                *Under an annual plan
              </div>
            </div>

            {/* 3rd Card */}
            <div className="stat-card">
              <span className="stat-num" data-count={100}>
                0<span style={{ fontSize: 20 }}>%</span>
              </span>
              <div className="stat-label">
                Repeat scans on a genuine QR code stay Verified
              </div>
            </div>

            {/* 4th Card */}
            {/* <div className="stat-card">
              <span className="stat-num" data-count={20}>
                <span className="stat-prefix">~</span>0<span style={{ fontSize: 20 }}>sec</span>
              </span>
              <div className="stat-label">
                Typical time to authenticate our QRmor code
              </div>
            </div> */}

            <div className="stat-card">
              <span className="stat-num">
                Few seconds
              </span>
              <div className="stat-label">
                To authenticate our QRmor code
              </div>
            </div>

          </div>

          <div className="stats-note">
            Map and log entries shown are illustrative sample data.
          </div>
        </div>
      </section>
      <section id="verification-map" className="reveal">
        <div className="wrap">
          {/* <div className="section-head section-head-wide">
            <span className="eyebrow">Live activity</span>
            <h2>See verification activity as it happens.</h2>
            <p>Every scan — genuine or flagged — is logged with its location.</p>
          </div> */}
          <div className="verification-showcase">
            <HeroShowcase />
            <div className="dashboard-preview">
              <img src="/assets/QRmor_Analytics.png" alt="QRmor authentication dashboard showing total authentications, alerts, and device breakdown" />
            </div>
          </div>
        </div>
      </section>
      <section className="reveal">
        <div className="wrap">
          <div className="mandate-banner">
            <div>
              <span className="eyebrow">Why brands are moving now</span>
              <h2>The mandate isn't coming. It's already here.</h2>
              {/* <p>Regulators across industries are moving toward mandatory product-level traceability — and pharma is furthest along. India has required QR or barcodes on its top pharma brands since 2023, and on every active pharmaceutical ingredient since January 2023. In 2026, that requirement widened to cover all vaccines, antimicrobials, anti-cancer medicines, and narcotic and psychotropic drugs under Schedule H2. Several states are rolling out similar QR track-and-trace mandates for alcohol, to curb smuggling and counterfeit liquor — and agritech, automobiles, and luxury goods are following the same pattern for the same reason: a fake product is a real cost. QRmor is built for exactly this kind of requirement, in whichever industry it shows up.</p> */}
              <p>
                Regulators across industries are moving toward mandatory product-level
                security and traceability.
              </p>
              <ul className="mandate-points">

                <li>
                  Pharma — Government mandated since 2023.
                </li>
                <li>
                  Pharma — Schedule H2 mandated (all vaccines, antimicrobials, anti-cancer medicines, and narcotic and psychotropic drugs).
                </li>

                <li>
                  QRmor is built for exactly this kind of requirement, in whichever industry
                  it shows up.
                </li>
              </ul>
              <div className="domain-pills">
                <span>Pharma — mandated</span>
                <span>Agritech</span>
                <span>Alcohol — mandated in several states</span>

                <span>automobiles</span>
                <span>Auto-Mobiles</span>
                <span>Luxury Brands</span>
              </div>
            </div>
            <div className="mandate-right">
              <div className="typewriter-banner">
                <span className="typewriter-text">Let QRmor protect Brands from Counterfeits</span>
              </div>
              <div className="mandate-quote">
                Every unit gets a unique code. Every scan is checked, logged, and mapped — turning a compliance mandate into a system of trust.
                — QRmor's approach to authentication
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <section className="reveal">
        <div className="wrap">
          <div className="typewriter-banner">
            <span className="typewriter-text">Let QRmor protect Brands from Counterfeits</span>
          </div>
        </div>
      </section> */}

      <section id="how" className="reveal">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">How it works</span>
            <h2>Plug and Play: Print QRmor codes in a few steps. </h2>
            <p>Plug and play for your business. No new hardware/software needed for your production line. Your customers can scan using their smartphones. No app required.</p>
          </div>
          <div className="steps">
            <div className="step">
              <span className="num">01</span>
              <h3>Request a license</h3>
              <p>Activate a QRmor license for the SKU you want to protect.</p>
            </div>
            <div className="step">
              <span className="num">02</span>
              <h3>Add product (SKU) details</h3>
              <p>Register the product details that matter for verification.</p>
            </div>
            <div className="step">
              <span className="num">03</span>
              <h3>Generate QRmor code</h3>
              <p>Generate unique, non-clonable QRmor codes — each one dynamic, so a single code can serve multiple batches of the same SKU.</p>
            </div>
            <div className="step">
              <span className="num">04</span>
              <h3>Pre-printed conveinence</h3>
              <p>Add the QRmor code to the artwork of product labels (primary or secondary packaging materials) before production.
                <br /> <b>Saves Money, as there is no need to print QR codes online like L1, L2, etc.</b></p>
            </div>
            <div className="step">
              <span className="num">05</span>
              <h3>Add batch details</h3>
              <p>During production, link batch number, mfg/exp date and other traceability data to the pre-printed QRmor codes on the product labels and let <b>QRmor Intelligence</b> handle it.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* <section id="video-demo" class="reveal">
      <div class="wrap">
        <div class="video-wrap">
    <div class="video-side">
      <span class="eyebrow">See it live</span>
      <h2>Watch a real scan get verified.</h2>
      <p>Drop in a short recording of a genuine scan being checked in real time — and, if you have one, a cloned code being caught. Seeing the result appear in seconds does more than any paragraph on this page.</p>
      <div class="video-checklist">
        <div><svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> A genuine product scanned and verified</div>
        <div><svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> A cloned or copied code being flagged</div>
        <div><svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> The result appearing on-screen, start to finish</div>
      </div>
    </div>

    <div class="video-card">
      <div class="video-frame" id="videoFrame">
        <video id="verificationVideo" controls playsinline preload="metadata">
                 <source src="assets/verification-demo.mp4" type="video/mp4">
        </video>
        <div class="video-placeholder" id="videoPlaceholder">
          <div class="video-placeholder-inner">
            <div class="play-circle">
              <svg viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
            </div>
            <h4>Add your verification video here</h4>
            <p>Replace the <code>&lt;source src&gt;</code> in the code with your own file or hosted link, and this player will pick it up automatically.</p>
          </div>
        </div>
      </div>
      <div class="video-meta">
        <span class="label">DEMO VERIFICATION</span>
        <span class="label">00:00 placeholder</span>
      </div>
    </div>
        </div>
      </div>
    </section> */}

      <section className="reveal">

        <div className="wrap why-wrap">
          <div className="why-card">
            <div className="why-brand">
              <img src="/assets/Scinova.png" alt="Scinova Scientifics logo" />
              <span className="why-brand-name">Scinova Scientifics</span>
            </div>
            <p><b style={{ color: 'var(--ink)' }}>QRmor comes from the Scinova Scientifics and <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC®</a> (a SIRO Centre) team</b> — Research and Delivery under one roof.
              The people who built QRmor and it's authentication engine are the same people behind the audit logging and the support you'll actually talk to.</p>

            <a href="#contact" onClick={handleNavClick('contact')} className="btn btn-primary btn-sm" style={{ marginTop: 16 }}>Talk to the team</a>
          </div>
          <div className="why-list">
            <div className="why-item">
              <div className="icon-badge bg-teal" style={{ width: 40, height: 40 }}><svg viewBox="0 0 24 24" fill="none">
                <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.8" />
              </svg></div>
              <div>
                <h4>Setting the standard, not following one </h4>
                <p>QRmor isn't adapting an existing category—it's defining what non-clonable authentication looks like from the ground up.
                </p>
              </div>
            </div>
            <div className="why-item">
              <div className="icon-badge bg-gold" style={{ width: 40, height: 40 }}><svg viewBox="0 0 24 24" fill="none">
                <path d="M4 19V5m0 14h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg></div>
              <div>
                <h4>Ready for regulators</h4>
                <p>Every scan, genuine or flagged, is logged with time and location — a clean audit trail whenever it's
                  asked for.</p>
              </div>
            </div>
            <div className="why-item">
              <div className="icon-badge bg-navy" style={{ width: 40, height: 40 }}><svg viewBox="0 0 24 24" fill="none">
                <path d="M12 2l1.8 4.6L18 8l-4.2 1.4L12 14l-1.8-4.6L6 8l4.2-1.4L12 2z" stroke="currentColor" strokeWidth="1.6" />
              </svg></div>
              <div>
                <h4>One engine, many industries</h4>
                <p>The same authentication core already serves pharma, and carries over to agritech, automobiles, and luxury
                  goods.</p>
              </div>
            </div>
            <div className="why-item">
              <div className="icon-badge bg-teal" style={{ width: 40, height: 40 }}><svg viewBox="0 0 24 24" fill="none">
                <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg></div>
              <div>
                <h4>Built to move fast</h4>
                <p>Custom deliveries, low turnaround, and first access to new releases — the kind of responsiveness a larger, more layered vendor can't match.</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section id="features" className="reveal">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">The advantages</span>
            <h2>What this actually gets you.</h2>
          </div>
          <div className="advantage-grid">
            <div className="advantage-card teal-tint">
              <span className="num-mark">01</span>
              <h3>See counterfeits as they happen
              </h3>
              <p>Every flagged scan is logged with its location, so you know where suspicious activity is surfacing and can investigate.</p>
            </div>
            <div className="advantage-card gold-tint">
              <span className="num-mark">02</span>
              <h3>Build a brand people trust</h3>
              <p>When customers can check a product themselves and always get a straight answer, that confidence becomes
                part of the brand.</p>
            </div>
            <div className="advantage-card navy-tint">
              <span className="num-mark">03</span>
              <h3>Compliance without the paperwork chase</h3>
              <p>Every validation is logged automatically, so an audit trail is always ready — no manual reporting when a
                regulator asks.</p>
            </div>
            <div className="advantage-card crimson-tint">
              <span className="num-mark">04</span>
              <h3>Cut the cost of counterfeits</h3>
              <p>Catching fakes early limits the damage — fewer recalls, less liability, and less revenue lost to knockoffs.
              </p>
            </div>
            <div className="advantage-card teal-tint">
              <span className="num-mark">05</span>
              <h3>Real-time alerts, not quarterly reports</h3>
              <p>Flagged scans surface as they happen, so your team can watch for a genuine cluster forming — not chase every single misread — and act the same day a real pattern shows up.</p>
            </div>
            <div className="advantage-card gold-tint">
              <span className="num-mark">06</span>
              <h3>One system across every market</h3>
              <p>Wherever your product sells, the same verification and logging works.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="why" className="reveal">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">Why teams choose QRmor</span>
            <h2>Authentication that holds up in the real world.</h2>
            <p>Counterfeits don't appear in a lab. QRmor is built to verify wherever your product actually moves — warehouses, market stalls, and everywhere in between.</p>
          </div>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="icon-badge bg-teal">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Genuine stays genuine</h3>
              <p>An authentic QRmor code validates on every scan — first time or hundredth time. No false alarms for a product
                changing hands.</p>
            </div>
            <div className="feature-card">
              <div className="icon-badge bg-gold">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 7l6 3.5m0 0L16 7m-6 3.5V17M20 7l-6 3.5m6-3.5v6.9L14 17M4 7v6.9L10 17M20 7l-6-3.5-6 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Clone detection</h3>
              <p>A cloned or duplicated QR code is recognized as fake the moment it's scanned, wherever it surfaces.</p>
            </div>
            <div className="feature-card">
              <div className="icon-badge bg-crimson">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 19V5m0 14h16M8 19v-6m4 6v-9m4 9v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Full audit trail</h3>
              <p>Every validation — genuine or flagged — is logged with a timestamp and location for compliance and
                reporting.</p>
            </div>
            <div className="feature-card">
              <div className="icon-badge bg-navy">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l1.8 4.6L18 8l-4.2 1.4L12 14l-1.8-4.6L6 8l4.2-1.4L12 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  <path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>SKU wise Licensing &amp; QRmor code generation</h3>
              <p>
                License and generate secure, non-clonable QRmor codes SKU by SKU — scale up as new product lines need protection, without a blanket company-wide commitment.</p>
            </div>
            <div className="feature-card">
              <div className="icon-badge bg-teal">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16v12H8l-4 4V4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Support built in</h3>
              <p>Anyone with a question about a scan result can reach real support from the same screen.</p>
            </div>
            <div className="feature-card">
              <div className="icon-badge bg-gold">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 21s-7-4.5-9.5-9C1 8 2.5 4.5 6 4c2-.3 4 1 6 3 2-2 4-3.3 6-3 3.5.5 5 4 3.5 8-2.5 4.5-9.5 9-9.5 9z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Works with minimal bandwidth</h3>
              <p>Verification runs even on a slow connection, which doesn't stop a rural customer or a warehouse
                to scan.</p>
            </div>
          </div>
        </div>
      </section>
      <section id="usecases" className="reveal">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">Who it's for</span>
            <h2>"A Product worth copying is A Product worth Protecting"</h2>
            <p>Some of these industries already have a mandate behind them. In every one of them, a fake product is a real
              damage.</p>
          </div>
          <div className="usecase-row">
            <div className="usecase">
              <div className="icon-badge bg-crimson"><svg viewBox="0 0 24 24" fill="none">
                <path d="M8.5 8.5l7 7M9 15l6-6a3 3 0 114 4l-6 6a3 3 0 01-4-4z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg></div>
              <h4>Pharma &amp; Healthcare</h4>
            </div>
            <div className="usecase">
              <div className="icon-badge bg-teal"><svg viewBox="0 0 24 24" fill="none">
                <path d="M12 3c3 3 5 6 5 9a5 5 0 01-10 0c0-3 2-6 5-9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg></div>
              <h4>Agritech</h4>
            </div>
            <div className="usecase">
              <div className="icon-badge bg-gold"><svg viewBox="0 0 24 24" fill="none">
                <path d="M9 3h6v4l3 3v9a2 2 0 01-2 2H8a2 2 0 01-2-2v-9l3-3V3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                <path d="M6.5 13h11" stroke="currentColor" strokeWidth="1.5" />
              </svg></div>
              <h4>Alcohol &amp; Beverages</h4>
            </div>


            <div className="usecase">
              <div className="icon-badge bg-navy"><svg viewBox="0 0 24 24" fill="none">
                <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg></div>
              <h4>Auto-Mobiles</h4>
            </div>
            <div className="usecase">
              <div className="icon-badge bg-crimson"><svg viewBox="0 0 24 24" fill="none">
                <path d="M12 2l3 6 6 1-4.5 4.4L17.5 20 12 17l-5.5 3 1-6.6L3 8l6-1 3-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg></div>
              <h4>Luxury Brands</h4>
            </div>
            <div className="usecase">
              <div className="icon-badge bg-gold"><svg viewBox="0 0 24 24" fill="none">
                <path d="M12 21s-6-4-6-9a6 6 0 0112 0c0 5-6 9-6 9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                <circle cx={12} cy={11} r="2.2" stroke="currentColor" strokeWidth="1.4" />
              </svg></div>
              <h4>Cosmetics &amp; Personal care</h4>
            </div>
            <div className="usecase">
              <div className="icon-badge bg-teal"><svg viewBox="0 0 24 24" fill="none">
                <path d="M6 3h9l3 4v14H6V3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                <path d="M9 10h6M9 14h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg></div>
              <h4>FMCG &amp; Packaged foods</h4>
            </div>
            <div className="usecase">
              <div className="icon-badge bg-navy"><svg viewBox="0 0 24 24" fill="none">
                <path d="M7 3h10l1 5-6 3-6-3 1-5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                <path d="M6 21V11M18 21V11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg></div>
              <h4>Documents &amp; Certificates</h4>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="wrap">
          <div className="cta-band">
            <div>
              <h2>Ready to make counterfeits pointless?</h2>
              <p>Tell us about your product line — pharma, agritech, automobiles, or luxury — and we'll show you a rollout
                built around it.</p>
            </div>
            <a href="#contact" onClick={handleNavClick('contact')} className="btn btn-primary">Request a demo</a>
          </div>
        </div>
      </section>

      {/* About us section

      <section id="about" className="reveal about-section">
        <div className="wrap">
          <div className="about-grid">
            <div className="about-copy">
              <span className="eyebrow">Company</span>
              <h2>About Us</h2>
              <p>
                QRmor comes from Scinova Scientifics, a scientific services startup focused on
                pharma, healthcare, and agri-tech, built under <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC®</a> — the Centre for Incubation,
                Innovation, Research and Consultancy, a Section 8 Scientific and Industrial
                Research Organization recognized by DSIR, Government of India.
              </p>
              <p>
               <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC</a>'s scientists invented the anti-counterfeit, non-clonable QRmor code
                generation technology and the deep-tech-based authentication engine at the core
                of the product.
              </p>
              <ul className="about-points">
                <li><b>Built from research, not retrofitted</b> — QRmor wasn't added on top of an existing QR product as an afterthought; the non-clonable technology was co-invented from the ground up at <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC</a>.</li>
                <li><b>One engine, many industries, from day one</b> — the same authentication core was designed to work across pharma, agritech, and beyond, not built narrowly for a single sector and adapted later.</li>
                <li><b>ISO 27001:2022 certified</b>, reflecting the same institutional rigor QRmor was built under.</li>
                <li><b>Built for global reach</b> — services designed for the global community, not just a single market.</li>
              </ul>
            </div>

            <div className="about-trust">
              <div className="footer-brand">
                <img src="/assets/QRmor.png" alt="QRmor" />
                <span className="wordmark">QRmor</span>
                <div className="divider" />
                <img src="/assets/Scinova.png" alt="Scinova Scientifics logo" className="brand-scinova-logo" />
                <div className="brand-scinova-text">
                  <span className="parent">Scinova Scientifics</span>
                  <span className="brand-tagline"> -Science as a Service</span>
                </div>
              </div>
              <div className="footer-reach-block">
                <span className="footer-reach-heading"><b>Why brands trust QRmor</b></span>
                <div className="footer-reach-items">
                  <span><span className="dot" /> Backed by a DSIR recognized R&D Centre, <b>CIIRC®</b></span>
                  <span><span className="dot" /> Built for regulated industries — pharma, agritech, automobile and more</span>
                  <span><span className="dot" /> No app, no hardware, just a smartphone scan</span>
                  <span><span className="dot" /> Research-driven, delivery-focused</span>
                  <span><span className="dot" /> Serving domestic and international customers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <section id="contact" className="reveal section-tight-top">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">Get started</span>
            <h2>Request a demo.</h2>
            <p>Tell us a bit about your company and we'll get back to you to walk through a rollout built around your
              product line.</p>
          </div>
          <div className="form-wrap">
            <div className="form-side">
              <h2>See it working on your own product.</h2>
              <p>A short walkthrough of license setup, QR generation, and the verification map — using an example from your
                industry.</p>
              <div className="form-perks">
                <div><svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg> No commitment, just a walkthrough</div>
                <div><svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg> Response within one business day</div>
                <div><svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg> Pharma, agritech, luxury, and more</div>
              </div>

            </div>
            <div className="demo-form">
              <div className="form-row">
                <div className="field">
                  <label htmlFor="fName">Name <span className="required-mark">*</span></label>
                  <input id="fName" type="text" placeholder="Your full name" autoComplete="name" required />
                  <span className="field-error" id="fNameError"></span>
                </div>
                <div className="field">
                  <label htmlFor="fCompany">Company name <span className="required-mark">*</span></label>
                  <input id="fCompany" type="text" placeholder="Your company" autoComplete="organization" required />
                  <span className="field-error" id="fCompanyError"></span>
                </div>
              </div>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="fEmail">Email <span className="required-mark">*</span></label>
                  <input id="fEmail" type="email" placeholder="you@company.com" autoComplete="email" required />
                  <span className="field-error" id="fEmailError"></span>
                </div>
                {/* <div className="field">
                  <label htmlFor="fPhone">Phone <span className="required-mark">*</span></label>
                  <input id="fPhone" type="tel" placeholder="+91 00000 00000" autoComplete="tel" required />
                  <span className="field-error" id="fPhoneError"></span>
                </div> */}
                <div className="field">
                  <label htmlFor="fPhone">Phone <span className="required-mark">*</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div ref={ccWrapRef} style={{ position: 'relative', flex: '0 0 90px' }}>
                      <button
                        type="button"
                        onClick={() => setCcOpen(o => !o)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: '14.5px',
                          padding: '12px 10px',
                          borderRadius: 11,
                          border: '1.5px solid var(--line)',
                          background: 'var(--bg)',
                          color: 'var(--ink)',
                          cursor: 'pointer'
                        }}
                      >
                        <span>{ccSelected.flag} {ccSelected.code}</span>
                        <span style={{ fontSize: 10, opacity: 0.6 }}>▼</span>
                      </button>

                      {ccOpen && (
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          width: 220,
                          maxHeight: 240,
                          overflowY: 'auto',
                          background: '#fff',
                          border: '1.5px solid var(--line)',
                          borderRadius: 11,
                          boxShadow: 'var(--shadow)',
                          zIndex: 20
                        }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Type code or country, e.g. 91 or India"
                            value={ccSearch}
                            onChange={(e) => setCcSearch(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              border: 'none',
                              borderBottom: '1px solid var(--line)',
                              outline: 'none',
                              fontSize: '13.5px'
                            }}
                          />
                          {filteredCodes.length === 0 && (
                            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ink-soft)' }}>
                              No match
                            </div>
                          )}
                          {filteredCodes.map((c, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                setCcSelected(c);
                                setCcOpen(false);
                                setCcSearch('');
                              }}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '9px 12px',
                                cursor: 'pointer',
                                fontSize: 14
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <span>{c.flag} {c.name}</span>
                              <span style={{ color: 'var(--ink-soft)', fontSize: 13 }}>{c.code}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <input
                      id="fPhone"
                      name="phone_local"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={10}
                      placeholder="9974051262"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      required
                      style={{ flex: 1 }}
                    />
                  </div>
                  <span className="field-error" id="fPhoneError"></span>
                </div>
              </div>
              <div className="form-row">
                <div className="field full">
                  <label htmlFor="fMessage">Message <span className="field-optional"></span></label>
                  <textarea
                    id="fMessage"
                    placeholder="Tell us a bit about what you're looking for..."
                    rows={4}
                  />
                  <span className="field-error" id="fMessageError"></span>
                </div>
              </div>
              <button className="btn btn-primary" id="demoSubmit">Request a demo</button>
              <div className="form-status" id="formStatus" />
            </div>
          </div>
        </div>
      </section>

      <section className="reveal">
        <div className="wrap what-next-row">

          <div className="what-next-image">
            <img
              src="/assets/WhatHappensNext 1.png"
              alt="What happens next with QRmor: review your product line, get a reply within one business day, and roll out a solution built around your SKU"
            />
          </div>

          <div className="compliance-strip">
            <span className="compliance-label">
              Certified &amp; Compliant
            </span>

            <div className="compliance-badges">
              <img
                src="/assets/ISO-9001.png"
                alt="ISO 27001 certified"
              />
              <img
                src="/assets/Gamp11.png"
                alt="Complies with GAMP and 21 CFR Part 11 guidelines"
              />
            </div>
          </div>

        </div>
      </section>



      <footer>
        <div className="wrap footer-wrap">
          {/* <div className="footer-left">
            <div className="footer-brand">
              <img src="/assets/QRmor.png" alt="QRmor" />
              <span className="wordmark">QRmor</span>
              <div className="divider" />
              <img src="/assets/Scinova.png" alt="Scinova Scientifics logo" className="brand-scinova-logo" />
              <div className="brand-scinova-text">
                <span className="parent">Scinova Scientifics</span>
                <span className="brand-tagline"> -Science as a Service</span>
              </div>
            </div>
            <div className="footer-reach-block">
              <span className="footer-reach-heading"><b>Why brands trust QRmor</b></span>
              <div className="footer-reach-items">
                <span><span className="dot" /> Backed by a SIRO-recognized R&D Centre,<b>CIIRC®</b></span>
                <span><span className="dot" /> Built for regulated industries — pharma, agritech, automobile and more</span>
                <span><span className="dot" /> No app, no hardware, just a smartphone scan</span>
                <span><span className="dot" /> Research-driven, delivery-focused</span>
                <span><span className="dot" /> Serving domestic and international customers</span>
              </div>
            </div> */}
          <div className="footer-left">
            <div className="footer-brand">
              <img src="/assets/QRmor.png" alt="QRmor" />
              <span className="wordmark">QRmor</span>
              <div className="divider" />
              <img src="/assets/Scinova.png" alt="Scinova Scientifics logo" className="brand-scinova-logo" />
              <div className="brand-scinova-text">
                <span className="parent">Scinova Scientifics</span>
                <span className="brand-tagline"> -Science as a Service</span>
              </div>
            </div>
            <div className="footer-reach-block">
              <span className="footer-reach-heading"><b>Why brands trust QRmor</b></span>
              <div className="footer-reach-items">
                <span><span className="dot" /> Backed by a DSIR recognized R&D Centre, <b><a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC®</a></b></span>
                <span><span className="dot" /> Built for regulated industries — pharma, agritech, automobile and more</span>
                <span><span className="dot" /> No app, no hardware, just a smartphone scan</span>
                <span><span className="dot" /> Research-driven, delivery-focused</span>
                <span><span className="dot" /> Serving domestic and international customers</span>
              </div>
            </div>
          </div>
          <div className="footer-cols">
            <div className="footer-col">
              <h5>Product</h5>
              <a href="#how">How it works</a>
              <a href="#features">Features</a>
              <a href="#usecases">Use cases</a>
              <Link to="/technology">Technology</Link>
              <Link to="/solutions">Solutions</Link>

            </div>
            <div className="footer-col">
              <h5>Company</h5>
              <Link to="/about-us">Know about us</Link>
              <Link to="/faq">FAQ</Link>
              <Link to="/why-normal-qr-fails">Why Normal QR Fails</Link>
            </div>
            <div className="footer-col">
              <h5>Legal</h5>
              <Link to="/terms-of-service">Terms of Service</Link>
              <Link to="/privacy-policy">Privacy Policy</Link>
            </div>
            <div className="footer-col">
              <h5>Contact Us</h5>
              <a href="mailto:contact@qrmor.com">contact@qrmor.com</a>
              <p className="footer-address">
                Scinova Scientifics,
                A unit of Gurukrupa Enterprise,<br />
                609, Athens, opp. Zanshi Rani Statue,
                Shiv Ranjani Cross Roads, Satellite,<br />
                Ahmedabad, India – 380015,<br />
                <b>Mobile: +91-9974051262</b>
              </p>
            </div>
          </div>
        </div>
        <div className="wrap footer-bottom">
          <span>© 2026 Scinova Scientifics. QRmor™ is a trademark of Scinova Scientifics.</span>
          <span>Authenticity, one scan at a time.</span>
        </div>
      </footer >
    </>
  );
}
